/** Shared wheel transaction snapshot cache (History, Stocks share history, mobile). */

import { fetchCallsPlusTransactions } from "./callsPlus";
import { loadOwnLedgerTransactionSnapshot, type LedgerSource } from "./ownLedgerClient";

export type WheelTransactionSnapshot = Awaited<ReturnType<typeof fetchCallsPlusTransactions>>;

export type { LedgerSource };

const TRANSACTIONS_CACHE_MS = 3 * 60_000;

type SnapshotCache = {
  data: WheelTransactionSnapshot | null;
  at: number;
  inflight: Promise<WheelTransactionSnapshot> | null;
};

const caches: Record<LedgerSource, SnapshotCache> = {
  stockforge: { data: null, at: 0, inflight: null },
  own_ledger: { data: null, at: 0, inflight: null },
};

export function invalidateTransactionsSnapshot(source?: LedgerSource): void {
  if (source) {
    caches[source] = { data: null, at: 0, inflight: null };
    return;
  }
  caches.stockforge = { data: null, at: 0, inflight: null };
  caches.own_ledger = { data: null, at: 0, inflight: null };
}

export function isTransactionsSnapshotFresh(
  source: LedgerSource = "stockforge",
  maxAgeMs = TRANSACTIONS_CACHE_MS,
): boolean {
  const cache = caches[source];
  return Boolean(cache.data && Date.now() - cache.at < maxAgeMs);
}

export async function loadTransactionsSnapshot(
  get: <T>(path: string, force?: boolean) => Promise<T>,
  force = false,
  source: LedgerSource = "stockforge",
): Promise<WheelTransactionSnapshot> {
  const cache = caches[source];
  const now = Date.now();
  if (!force && cache.data && now - cache.at < TRANSACTIONS_CACHE_MS) {
    return cache.data;
  }
  if (cache.inflight && !force) {
    return cache.inflight;
  }

  const fetchSnapshot = async (): Promise<WheelTransactionSnapshot> => {
    try {
      const txns =
        source === "own_ledger"
          ? await loadOwnLedgerTransactionSnapshot(force)
          : await fetchCallsPlusTransactions(get, force);
      cache.data = txns;
      cache.at = Date.now();
      return txns;
    } catch (err) {
      if (cache.data) return cache.data;
      throw err;
    } finally {
      cache.inflight = null;
    }
  };

  cache.inflight = fetchSnapshot();
  return cache.inflight;
}
